import React, { useState, useEffect } from 'react';
import { studentsAPI, documentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import StudentDocuments from './StudentDocuments';
import PartnerProgressPanel from './PartnerProgressPanel';
import ClientStepsPanel from './ClientStepsPanel';
import LessonsPanel from './LessonsPanel';
import './StudentModal.css';

// ─── Transfer-to-Alumni confirmation dialog ──────────────────────────────────
const TransferAlumniDialog = ({ studentName, onConfirm, onCancel, loading }) => (
  <div className="modal-overlay" onClick={onCancel} style={{ zIndex: 2000 }}>
    <div
      className="modal-content"
      style={{ maxWidth: 420, padding: 28 }}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 style={{ margin: '0 0 12px', color: '#011FF3' }}>🎓 Transfer to Alumni</h3>
      <p style={{ color: '#444', lineHeight: 1.5, margin: '0 0 20px' }}>
        This will create an <strong>Alumni record</strong> for{' '}
        <strong>{studentName}</strong> and mark their quest as fully complete.
        The student record is kept — it will not be deleted.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn-secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button
          className="btn-primary"
          style={{ background: '#011FF3' }}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Transferring…' : 'Yes, Transfer'}
        </button>
      </div>
    </div>
  </div>
);

// Format ISO date string → YYYY-MM-DD for <input type="date">
const toDateInput = (val) => {
  if (!val) return '';
  const d = new Date(val);
  return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

const StudentModal = ({ student, agents, onClose, onSave }) => {
  const { isAdmin, isAgentOrEmployee, isSalesLeadership, canSetPaymentStatus } = useAuth();
  const canSetPayment = canSetPaymentStatus();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    status: 'NEW',
    assigned_agent: '',
    payment_status: 'NOT_PAID',
    country: '',
    destination_country: '',
    city: '',
    university: '',
    notes: '',
    reference_by: '',
    referral_reward_issued: false,
    mother_name: '',
    father_name: '',
    parent_contact: '',
    passport_number: '',
    application_type: '',
    package: '',
    preferred_course_level: '',
    date_of_birth: '',
    date_docs_complete: '',
    date_visa_complete: '',
    quest_documents_sent: false,
    quest_application_submitted: false,
    quest_admission: false,
    quest_visa_approval: false,
    quest_arrived_turkey: false,
    quest_registration_done: false,
    quest_residence_permit: false,
    quest_equivalence_letter: false,
    quest_became_alumni: false,
    waiting_on_override: '',
  });
  const [showAdmissionsSection, setShowAdmissionsSection] = useState(false);
  const [showProgramMatches, setShowProgramMatches] = useState(false);
  const [programMatches, setProgramMatches] = useState([]);
  const [academicProfile, setAcademicProfile] = useState(null);
  const [loadingProgramMatches, setLoadingProgramMatches] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferSuccess, setTransferSuccess] = useState(false);
  const [loginSetup, setLoginSetup] = useState(null); // { token, link, expires_hours }
  const [issuingLoginSetup, setIssuingLoginSetup] = useState(false);
  const [collaboratorIds, setCollaboratorIds] = useState([]); // multi-assign: co-workers who can also see this student
  const [commissionMode, setCommissionMode] = useState('manual'); // 'manual' | 'percent'
  const [commission, setCommission] = useState({ amount: '', base: '', percent: '' });
  const toggleCollaborator = (id) => setCollaboratorIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  );

  useEffect(() => {
    const loadStudentData = async () => {
      if (student) {
        try {
          // Fetch full student details to ensure we have all data
          const response = await studentsAPI.get(student.id);
          const fullStudent = response.data;
          
          // Store the full student object for reference
          setCurrentStudent(fullStudent);
          
          // Format date_of_birth for input field (YYYY-MM-DD)
          let formattedDate = '';
          if (fullStudent.date_of_birth) {
            const date = new Date(fullStudent.date_of_birth);
            if (!isNaN(date.getTime())) {
              formattedDate = date.toISOString().split('T')[0];
            }
          }
          
          // Handle assigned_agent - could be ID or object
          let assignedAgentId = '';
          if (fullStudent.assigned_agent) {
            if (typeof fullStudent.assigned_agent === 'object' && fullStudent.assigned_agent !== null) {
              assignedAgentId = fullStudent.assigned_agent.id || fullStudent.assigned_agent;
            } else {
              assignedAgentId = fullStudent.assigned_agent;
            }
          }
          
          setFormData({
            first_name: fullStudent.first_name || '',
            last_name: fullStudent.last_name || '',
            email: fullStudent.email || '',
            phone: fullStudent.phone || '',
            status: fullStudent.status || 'NEW',
            assigned_agent: assignedAgentId || '',
            payment_status: fullStudent.payment_status || 'NOT_PAID',
            country: fullStudent.country || '',
            destination_country: fullStudent.destination_country || '',
            city: fullStudent.city || '',
            university: fullStudent.university || '',
            notes: fullStudent.notes || '',
            reference_by: fullStudent.reference_by || '',
            referral_reward_issued: !!fullStudent.referral_reward_issued,
            mother_name: fullStudent.mother_name || '',
            father_name: fullStudent.father_name || '',
            parent_contact: fullStudent.parent_contact || '',
            passport_number: fullStudent.passport_number || '',
            application_type: fullStudent.application_type || '',
            package: fullStudent.package || '',
            preferred_course_level: fullStudent.preferred_course_level || '',
            date_of_birth: formattedDate,
            date_docs_complete: toDateInput(fullStudent.date_docs_complete),
            date_visa_complete: toDateInput(fullStudent.date_visa_complete),
            quest_documents_sent: !!fullStudent.quest_documents_sent,
            quest_application_submitted: !!fullStudent.quest_application_submitted,
            quest_admission: !!fullStudent.quest_admission,
            quest_visa_approval: !!fullStudent.quest_visa_approval,
            quest_arrived_turkey: !!fullStudent.quest_arrived_turkey,
            quest_registration_done: !!fullStudent.quest_registration_done,
            quest_residence_permit: !!fullStudent.quest_residence_permit,
            quest_equivalence_letter: !!fullStudent.quest_equivalence_letter,
            quest_became_alumni: !!fullStudent.quest_became_alumni,
            waiting_on_override: fullStudent.waiting_on_override || '',
          });
          setCollaboratorIds((fullStudent.assigned_agents_detail || []).map((a) => String(a.id)));
          setCommission({
            amount: fullStudent.commission_amount ?? '',
            base: fullStudent.commission_base_amount ?? '',
            percent: fullStudent.commission_percent ?? '',
          });
          setCommissionMode(
            fullStudent.commission_base_amount != null && fullStudent.commission_percent != null
              ? 'percent' : 'manual'
          );
        } catch (error) {
          console.error('Error loading student data:', error);
          // Fallback to using the student object directly if API call fails
          setCurrentStudent(student);
          
          let formattedDate = '';
          if (student.date_of_birth) {
            const date = new Date(student.date_of_birth);
            if (!isNaN(date.getTime())) {
              formattedDate = date.toISOString().split('T')[0];
            }
          }
          
          let assignedAgentId = '';
          if (student.assigned_agent) {
            if (typeof student.assigned_agent === 'object' && student.assigned_agent !== null) {
              assignedAgentId = student.assigned_agent.id || student.assigned_agent;
            } else {
              assignedAgentId = student.assigned_agent;
            }
          }
          
          setFormData({
            first_name: student.first_name || '',
            last_name: student.last_name || '',
            email: student.email || '',
            phone: student.phone || '',
            status: student.status || 'NEW',
            assigned_agent: assignedAgentId || '',
            payment_status: student.payment_status || 'NOT_PAID',
            country: student.country || '',
            destination_country: student.destination_country || '',
            city: student.city || '',
            university: student.university || '',
            notes: student.notes || '',
            reference_by: student.reference_by || '',
            referral_reward_issued: !!student.referral_reward_issued,
            mother_name: student.mother_name || '',
            father_name: student.father_name || '',
            parent_contact: student.parent_contact || '',
            passport_number: student.passport_number || '',
            application_type: student.application_type || '',
            package: student.package || '',
            preferred_course_level: student.preferred_course_level || '',
            date_of_birth: formattedDate,
            date_docs_complete: toDateInput(student.date_docs_complete),
            date_visa_complete: toDateInput(student.date_visa_complete),
            quest_documents_sent: !!student.quest_documents_sent,
            quest_application_submitted: !!student.quest_application_submitted,
            quest_admission: !!student.quest_admission,
            quest_visa_approval: !!student.quest_visa_approval,
            quest_arrived_turkey: !!student.quest_arrived_turkey,
            quest_registration_done: !!student.quest_registration_done,
            quest_residence_permit: !!student.quest_residence_permit,
            quest_equivalence_letter: !!student.quest_equivalence_letter,
            quest_became_alumni: !!student.quest_became_alumni,
            waiting_on_override: student.waiting_on_override || '',
          });
          setCollaboratorIds((student.assigned_agents_detail || []).map((a) => String(a.id)));
          setCommission({
            amount: student.commission_amount ?? '',
            base: student.commission_base_amount ?? '',
            percent: student.commission_percent ?? '',
          });
          setCommissionMode(
            student.commission_base_amount != null && student.commission_percent != null
              ? 'percent' : 'manual'
          );
        }
      } else {
        // Reset form when creating new student
        setCurrentStudent(null);
        setCollaboratorIds([]);
        setCommission({ amount: '', base: '', percent: '' });
        setCommissionMode('manual');
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          status: 'NEW',
          assigned_agent: '',
          payment_status: 'NOT_PAID',
          country: '',
          destination_country: '',
          city: '',
          university: '',
          notes: '',
          reference_by: '',
          mother_name: '',
          father_name: '',
          parent_contact: '',
          passport_number: '',
          application_type: '',
          package: '',
          preferred_course_level: '',
          date_of_birth: '',
          date_docs_complete: '',
          date_visa_complete: '',
          quest_documents_sent: false,
          quest_application_submitted: false,
          quest_admission: false,
          quest_visa_approval: false,
          quest_arrived_turkey: false,
          quest_registration_done: false,
          quest_residence_permit: false,
          quest_equivalence_letter: false,
          quest_became_alumni: false,
          waiting_on_override: '',
        });
      }
    };

    loadStudentData();
  }, [student]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await studentsAPI.getCountries();
        setCountries(response.data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const fetchProgramMatches = async () => {
    if (!student || !student.id) return;
    setLoadingProgramMatches(true);
    try {
      const res = await studentsAPI.programMatches(student.id);
      setAcademicProfile(res.data.academic_profile);
      setProgramMatches(res.data.matches || []);
    } catch (err) {
      console.error('Error fetching program matches:', err);
    } finally {
      setLoadingProgramMatches(false);
    }
  };

  const handleToggleProgramMatches = () => {
    const next = !showProgramMatches;
    setShowProgramMatches(next);
    if (next && programMatches.length === 0) {
      fetchProgramMatches();
    }
  };

  const handleVerifyDocument = async (docId) => {
    try {
      await documentsAPI.verify(docId, '');
      // Trigger re-fetch by toggling an event — StudentDocuments will reload on its own
    } catch (err) {
      console.error('Error verifying document:', err);
    }
  };

  const handleTransferToAlumni = async () => {
    if (!student?.id) return;
    setTransferring(true);
    try {
      await studentsAPI.transferToAlumni(student.id);
      setTransferSuccess(true);
      setShowTransferDialog(false);
      // Refresh parent list so the student row shows the alumni state
      onSave();
    } catch (err) {
      const msg = err.response?.data?.error || 'Transfer failed. Please try again.';
      setError(msg);
      setShowTransferDialog(false);
    } finally {
      setTransferring(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Prepare data for submission - clean up empty strings
      const submitData = { ...formData };
      
      // Convert empty strings to null for optional fields
      const optionalFields = ['phone', 'country', 'city', 'university', 'notes', 'reference_by', 'passport_number',
                             'mother_name', 'father_name', 'parent_contact',
                             'application_type', 'package', 'preferred_course_level', 'date_of_birth',
                             'date_docs_complete', 'date_visa_complete', 'assigned_agent'];
      
      optionalFields.forEach(field => {
        if (submitData[field] === '' || submitData[field] === null || submitData[field] === undefined) {
          submitData[field] = null;
        }
      });
      
      // Ensure assigned_agent is either null or a number
      if (submitData.assigned_agent === '' || submitData.assigned_agent === null) {
        submitData.assigned_agent = null;
      } else {
        submitData.assigned_agent = parseInt(submitData.assigned_agent, 10);
      }

      submitData.assigned_agent_ids = collaboratorIds.map((id) => parseInt(id, 10));

      // Commission: either a manual amount, or a base × % pair the server
      // computes commission_amount from (see Student.save()). Only one mode
      // is ever sent, so switching modes can't leave a stale value behind.
      if (commissionMode === 'percent') {
        submitData.commission_base_amount = commission.base === '' ? null : commission.base;
        submitData.commission_percent = commission.percent === '' ? null : commission.percent;
      } else {
        submitData.commission_amount = commission.amount === '' ? 0 : commission.amount;
        submitData.commission_base_amount = null;
        submitData.commission_percent = null;
      }

      if (student) {
        await studentsAPI.update(student.id, submitData);
      } else {
        await studentsAPI.create(submitData);
      }
      onSave();
    } catch (err) {
      console.error('Error saving student:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMessage = 'Error saving student';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Handle field-specific validation errors
        if (errorData.errors && typeof errorData.errors === 'object') {
          const errorMessages = [];
          for (const [key, value] of Object.entries(errorData.errors)) {
            if (Array.isArray(value)) {
              const fieldName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              errorMessages.push(`${fieldName}: ${value.join(', ')}`);
            } else if (typeof value === 'string') {
              errorMessages.push(value);
            }
          }
          errorMessage = errorMessages.length > 0 ? errorMessages.join('; ') : errorMessage;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          // Try to extract any error message from the response
          errorMessage = JSON.stringify(errorData);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Quest checkpoint fields in sequential order — must be completed in order
  const QUEST_FIELDS = [
    'quest_documents_sent',
    'quest_application_submitted',
    'quest_admission',
    'quest_visa_approval',
    'quest_arrived_turkey',
    'quest_registration_done',
    'quest_residence_permit',
    'quest_equivalence_letter',
    'quest_became_alumni',
  ];

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    const idx = QUEST_FIELDS.indexOf(name);
    // Non-quest checkboxes: toggle normally
    if (idx === -1) {
      setFormData({ ...formData, [name]: checked });
      return;
    }
    const updated = { ...formData };
    if (checked) {
      // Checking a stage → auto-check all prior stages too (sequential requirement)
      for (let i = 0; i <= idx; i++) updated[QUEST_FIELDS[i]] = true;
    } else {
      // Unchecking a stage → auto-uncheck all later stages too (sequential requirement)
      for (let i = idx; i < QUEST_FIELDS.length; i++) updated[QUEST_FIELDS[i]] = false;
    }
    setFormData(updated);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>{student ? 'Edit Student' : 'Create Student'}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error-message">{error}</div>}

          {(currentStudent?.student_id || student?.student_id) && (
            <div className="form-row">
              <div className="form-group">
                <label>Student ID</label>
                <input
                  type="text"
                  value={currentStudent?.student_id || student?.student_id || ''}
                  disabled
                  className="read-only-field"
                  style={{
                    background: '#f5f5f5',
                    color: '#011ff3',
                    fontWeight: '600',
                    fontFamily: 'Courier New, monospace'
                  }}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="NEW">New</option>
                <option value="CONTACTED">Contacted</option>
                <option value="INTERESTED">Interested</option>
                <option value="APPLIED">Applied</option>
                <option value="ENROLLED">Enrolled</option>
                <option value="REJECTED">Rejected</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth || ''}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Application Type</label>
              <select
                name="application_type"
                value={formData.application_type}
                onChange={handleChange}
              >
                <option value="">Select application type</option>
                <option value="SCHOLARSHIP">Scholarship</option>
                <option value="SELF_FUNDED">Self-Funded</option>
                <option value="TURKISH_LANGUAGE_VISA">Turkish Language Visa</option>
              </select>
            </div>
            <div className="form-group">
              <label>Package</label>
              <select
                name="package"
                value={formData.package}
                onChange={handleChange}
              >
                <option value="">Select a package</option>
                <option value="PREMIUM">Premium Package</option>
                <option value="IN_TURKEY">In-Turkey Package</option>
                <option value="LITE">Lite Package</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Level (Preferred)</label>
              <select
                name="preferred_course_level"
                value={formData.preferred_course_level}
                onChange={handleChange}
              >
                <option value="">Select level</option>
                <option value="Associate Degree">Associate Degree</option>
                <option value="Undergraduate">Undergraduate</option>
                <option value="Masters">Masters</option>
                <option value="PhD">PhD</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Payment Status</label>
              <select
                name="payment_status"
                value={formData.payment_status}
                disabled={!canSetPayment}
                onChange={handleChange}
              >
                <option value="NOT_PAID">Not Paid</option>
                <option value="PAID">Paid</option>
                <option value="PARTIAL">Partial / Installments</option>
                <option value="REFUNDED">Refunded</option>
              </select>
              {!canSetPayment && <small style={{ color: '#64748b' }}>Only the CFO, Chairman, CEO or COO changes this.</small>}
            </div>
            {agents.length > 0 && (
              <div className="form-group">
                <label>Assigned Agent/Employee</label>
                <select
                  name="assigned_agent"
                  value={formData.assigned_agent}
                  onChange={handleChange}
                >
                  <option value="">None</option>
                  {agents.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName || `${user.username} (${user.role === 'AGENT' ? 'Agent' : 'Employee'})`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {agents.length > 0 && (
            <div className="form-group">
              <label>Also assign (co-workers who can also see this student)</label>
              <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 8, maxHeight: 130, overflowY: 'auto' }}>
                {agents.filter((user) => String(user.id) !== String(formData.assigned_agent)).map((user) => (
                  <label key={user.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" style={{ width: 'auto' }}
                      checked={collaboratorIds.includes(String(user.id))}
                      onChange={() => toggleCollaborator(String(user.id))} />
                    {user.displayName || `${user.username} (${user.role === 'AGENT' ? 'Agent' : 'Employee'})`}
                  </label>
                ))}
              </div>
            </div>
          )}

          {isAdmin() && (
            <div className="form-group">
              <label>Commission</label>
              <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 400, cursor: 'pointer' }}>
                  <input type="radio" style={{ width: 'auto' }} name="commissionMode" checked={commissionMode === 'manual'}
                    onChange={() => setCommissionMode('manual')} />
                  Manual amount
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 400, cursor: 'pointer' }}>
                  <input type="radio" style={{ width: 'auto' }} name="commissionMode" checked={commissionMode === 'percent'}
                    onChange={() => setCommissionMode('percent')} />
                  Base amount × %
                </label>
              </div>
              {commissionMode === 'manual' ? (
                <input type="number" min="0" step="0.01" placeholder="0.00"
                  value={commission.amount}
                  onChange={(e) => setCommission((c) => ({ ...c, amount: e.target.value }))} />
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="number" min="0" step="0.01" placeholder="Base amount" style={{ flex: 1 }}
                    value={commission.base}
                    onChange={(e) => setCommission((c) => ({ ...c, base: e.target.value }))} />
                  <span style={{ color: '#666' }}>×</span>
                  <input type="number" min="0" max="100" step="0.5" placeholder="%" style={{ flex: 1 }}
                    value={commission.percent}
                    onChange={(e) => setCommission((c) => ({ ...c, percent: e.target.value }))} />
                  <span style={{ color: '#666' }}>=</span>
                  <strong style={{ minWidth: 80 }}>
                    {commission.base !== '' && commission.percent !== ''
                      ? `$${(parseFloat(commission.base) * parseFloat(commission.percent) / 100).toFixed(2)}`
                      : '—'}
                  </strong>
                </div>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Country</label>
              {loadingCountries ? (
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Loading countries..."
                  disabled
                />
              ) : (
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                >
                  <option value="">Select a country</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Mother's name</label>
              <input
                type="text"
                name="mother_name"
                value={formData.mother_name}
                onChange={handleChange}
                maxLength={100}
                placeholder="Full name"
              />
            </div>
            <div className="form-group">
              <label>Father's name</label>
              <input
                type="text"
                name="father_name"
                value={formData.father_name}
                onChange={handleChange}
                maxLength={100}
                placeholder="Full name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Parent / guardian contact</label>
              <input
                type="tel"
                name="parent_contact"
                value={formData.parent_contact}
                onChange={handleChange}
                maxLength={20}
                placeholder="e.g. +263 7XX XXX XXX"
              />
              <small style={{ color: '#888' }}>
                Needed for the application. Captured automatically from the website and app registration forms.
              </small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Destination country (for Community)</label>
              <input
                type="text"
                name="destination_country"
                value={formData.destination_country}
                onChange={handleChange}
                placeholder="e.g. Turkey"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>University</label>
              <input
                type="text"
                name="university"
                value={formData.university}
                onChange={handleChange}
                placeholder="e.g. Istanbul University"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Reference by</label>
            <input
              type="text"
              name="reference_by"
              value={formData.reference_by}
              onChange={handleChange}
              placeholder="Person or source who referred this student"
            />
          </div>

          {student?.referred_by_alumni_name && (
            <div className="form-group">
              <label>Referred by alumnus</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="text" value={student.referred_by_alumni_name} readOnly />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={!!formData.referral_reward_issued}
                    onChange={(e) => setFormData((prev) => ({ ...prev, referral_reward_issued: e.target.checked }))}
                  />
                  Reward issued
                </label>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Passport number</label>
            <input
              type="text"
              name="passport_number"
              value={formData.passport_number}
              onChange={handleChange}
              placeholder="As shown on the passport"
            />
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
            />
          </div>

          {/* Admissions Tracking Section */}
          {student && (
            <div style={{ margin: '16px 0' }}>
              <button
                type="button"
                onClick={() => setShowAdmissionsSection((v) => !v)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: showAdmissionsSection ? '12px 12px 0 0' : '12px',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                <span>📋 Application Tracking</span>
                <span style={{ fontSize: '12px', opacity: 0.7 }}>{showAdmissionsSection ? '▲ Collapse' : '▼ Expand'}</span>
              </button>
              {showAdmissionsSection && (
                <div style={{
                  background: 'rgba(15, 52, 96, 0.3)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px',
                  padding: '16px',
                }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label style={{ fontSize: '12px', color: '#aaa' }}>Date Documents Complete</label>
                      <input
                        type="date"
                        name="date_docs_complete"
                        value={formData.date_docs_complete || ''}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: '12px', color: '#aaa' }}>Date Visa Approved</label>
                      <input
                        type="date"
                        name="date_visa_complete"
                        value={formData.date_visa_complete || ''}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  {/* Read-only summary of current application state */}
                  {(formData.university || formData.application_type || formData.destination_country) && (
                    <div style={{
                      background: 'rgba(255,255,255,0.04)',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      marginTop: '8px',
                      fontSize: '13px',
                      color: '#ccc',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '6px',
                    }}>
                      {formData.university && (
                        <div><strong style={{ color: '#4facfe' }}>University:</strong> {formData.university}</div>
                      )}
                      {formData.destination_country && (
                        <div><strong style={{ color: '#4facfe' }}>Destination:</strong> {formData.destination_country}</div>
                      )}
                      {formData.application_type && (
                        <div><strong style={{ color: '#4facfe' }}>Type:</strong> {formData.application_type.replace(/_/g, ' ')}</div>
                      )}
                      {formData.preferred_course_level && (
                        <div><strong style={{ color: '#4facfe' }}>Level:</strong> {formData.preferred_course_level}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Quest Map Progress Checkpoints */}
          {student && (
            <div className="quest-checkpoints" style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: '12px',
              padding: '20px',
              margin: '16px 0',
            }}>
              <h3 style={{ color: '#fff', marginBottom: '16px', fontSize: '16px' }}>
                Quest Map Progress
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '4px' }}>
                Check the boxes below to update the student's quest map progress in the mobile app.
              </p>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginBottom: '12px' }}>
                Stages are sequential — checking a later stage auto-checks all prior stages, and unchecking an earlier stage clears all later stages.
              </p>
              {[
                { name: 'quest_documents_sent', label: '1. Documents & Information Received', emoji: '📋' },
                { name: 'quest_application_submitted', label: '2. Application Submitted', emoji: '📝' },
                { name: 'quest_admission', label: '3. Acceptance Letter Received', emoji: '🎓' },
                { name: 'quest_visa_approval', label: '4. Visa Approval', emoji: '✈️' },
                { name: 'quest_arrived_turkey', label: '5. Arrived in Destination Country', emoji: '🌍' },
                { name: 'quest_registration_done', label: '6. Registration Done', emoji: '✅' },
                { name: 'quest_residence_permit', label: '7. Residence Permit Approval', emoji: '🏛️' },
                { name: 'quest_equivalence_letter', label: '8. Equivalence Letter Approval', emoji: '📜' },
                { name: 'quest_became_alumni', label: '9. Became Alumni', emoji: '🎉' },
              ].map((checkpoint) => (
                <label key={checkpoint.name} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 12px',
                  marginBottom: '6px',
                  background: formData[checkpoint.name] ? 'rgba(74, 222, 128, 0.15)' : 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: formData[checkpoint.name] ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                  transition: 'all 0.2s',
                }}>
                  <input
                    type="checkbox"
                    name={checkpoint.name}
                    checked={!!formData[checkpoint.name]}
                    onChange={handleCheckboxChange}
                    style={{ width: '18px', height: '18px', accentColor: '#4ade80' }}
                  />
                  <span style={{ fontSize: '20px' }}>{checkpoint.emoji}</span>
                  <span style={{
                    color: formData[checkpoint.name] ? '#4ade80' : 'rgba(255,255,255,0.7)',
                    fontWeight: formData[checkpoint.name] ? '600' : '400',
                    fontSize: '14px',
                  }}>
                    {checkpoint.label}
                  </span>
                </label>
              ))}
            </div>
          )}

          {student && student.id && <ClientStepsPanel kind="STUDENT" clientId={student.id} leadSource={student.lead_source} leadPaid={student.lead_paid} />}
          {student && student.id && <div style={{ margin: '0 -20px' }}><LessonsPanel kind="student" clientId={student.id} /></div>}

          {/* Partner progress tracking: referrer consent + document checklist
              (brainstorms/2026-09-27-partner-progress-tracking.md) */}
          {student && student.id && (isAdmin() || isAgentOrEmployee()) && (
            <PartnerProgressPanel
              studentId={student.id}
              hasReferringPartner={currentStudent?.assigned_agent_detail?.role === 'AGENT'}
              initialConsentStatus={currentStudent?.referrer_consent_status}
              waitingOnOverride={formData.waiting_on_override}
              onWaitingOnOverrideChange={(value) => setFormData((prev) => ({ ...prev, waiting_on_override: value }))}
            />
          )}

          {/* Ranked Program Matches — AI-parsed academic profile scored
              against the WEBSITE program catalog (see academic_parsing app).
              Answers "which programs is this student academically a fit for".
              Scores and reasoning notes are always pre-computed (Celery, see
              academic_parsing/tasks.py) — this only ever reads what's stored. */}
          {student && student.id && (
            <div style={{ marginTop: '16px' }}>
              <button
                type="button"
                onClick={handleToggleProgramMatches}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'rgba(15, 52, 96, 0.5)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: showProgramMatches ? '12px 12px 0 0' : '12px',
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <span>🎯 Ranked Program Matches</span>
                <span style={{ fontSize: '12px', opacity: 0.7 }}>{showProgramMatches ? '▲ Collapse' : '▼ Expand'}</span>
              </button>
              {showProgramMatches && (
                <div style={{
                  background: 'rgba(15, 52, 96, 0.3)',
                  borderRadius: '0 0 12px 12px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderTop: 'none',
                  padding: '12px 16px',
                }}>
                  {loadingProgramMatches ? (
                    <p style={{ color: '#aaa', fontSize: '13px' }}>Loading matches...</p>
                  ) : !academicProfile ? (
                    <p style={{ color: '#aaa', fontSize: '13px' }}>
                      No academic document parsed yet — upload a transcript or certificate below to generate matches.
                    </p>
                  ) : academicProfile.status === 'FAILED' ? (
                    <p style={{ color: '#f56565', fontSize: '13px' }}>
                      ⚠️ The uploaded document could not be parsed. Try re-uploading a clearer scan.
                    </p>
                  ) : (
                    <>
                      {academicProfile.status === 'NEEDS_REVIEW' && (
                        <p style={{ color: '#f5a623', fontSize: '12px', marginBottom: '10px' }}>
                          ⚠️ Parsed with low confidence{academicProfile.confidence_note ? `: ${academicProfile.confidence_note}` : ' — verify the values below manually.'}
                        </p>
                      )}
                      {programMatches.length === 0 ? (
                        <p style={{ color: '#aaa', fontSize: '13px' }}>No program matches above the relevance threshold yet.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {programMatches.map((match) => {
                            const tierColor = match.tier === 'SAFETY' ? '#43e97b' : match.tier === 'MATCH' ? '#f5a623' : '#011ff3';
                            return (
                              <div key={match.id} style={{
                                background: 'rgba(1, 31, 243,0.12)',
                                borderRadius: '8px',
                                padding: '10px 12px',
                                borderLeft: `4px solid ${tierColor}`,
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <strong style={{ fontSize: '13px', color: '#fff' }}>
                                    {match.program.program_name} — {match.program.university_name}
                                  </strong>
                                  <span style={{ fontSize: '12px', fontWeight: 700, color: tierColor }}>
                                    {match.fit_score}% ({match.tier})
                                  </span>
                                </div>
                                {match.reasoning_notes && (
                                  <p style={{ fontSize: '12px', color: '#bbb', marginTop: '6px', marginBottom: 0 }}>
                                    {match.reasoning_notes}
                                  </p>
                                )}
                                {match.follow_up_draft && (
                                  <details style={{ marginTop: '6px' }}>
                                    <summary style={{ fontSize: '11px', color: '#4facfe', cursor: 'pointer' }}>Suggested follow-up</summary>
                                    <p style={{ fontSize: '12px', color: '#ccc', marginTop: '4px', whiteSpace: 'pre-wrap' }}>{match.follow_up_draft}</p>
                                  </details>
                                )}
                                <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '4px', marginTop: '8px' }}>
                                  <div style={{ width: `${match.fit_score}%`, height: '4px', borderRadius: '4px', background: tierColor }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Documents Section - Only show when editing existing student */}
          {student && student.id && (
            <StudentDocuments
              studentId={student.id}
              canEdit={isAdmin() || isAgentOrEmployee()}
            />
          )}

          {/* ── Data subject (KVKK) ──────────────────────────────────────── */}
          {student && student.id && (isAdmin() || isAgentOrEmployee()) && (
            <div style={{ margin: '16px 0 0', padding: '14px 16px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, background: 'rgba(15,52,96,0.25)' }}>
              <div style={{ color: '#aaa', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 }}>DATA SUBJECT (KVKK)</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" onClick={async () => {
                  try {
                    const res = await studentsAPI.exportData(student.id);
                    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `student-${student.student_id || student.id}-data.json`; a.click();
                    URL.revokeObjectURL(url);
                  } catch { alert('Export failed.'); }
                }} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #2a2a3d', background: 'transparent', color: '#ddd', cursor: 'pointer', fontSize: 13 }}>
                  Export this student's data (JSON)
                </button>
                {isAdmin() && (
                  <button type="button" onClick={async () => {
                    if (!window.confirm('Irreversibly ERASE this data subject? Personal fields are wiped and document files deleted. Operational fields (ID, status, commission) are kept.')) return;
                    if (!window.confirm('This cannot be undone. Type-confirm by clicking OK once more.')) return;
                    try { await studentsAPI.erase(student.id); alert('Erased.'); onClose(); }
                    catch { alert('Erase failed.'); }
                  }} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #f5576c55', background: 'rgba(245,87,108,0.08)', color: '#f5576c', cursor: 'pointer', fontSize: 13 }}>
                    Erase data subject
                  </button>
                )}
              </div>
              <p style={{ color: '#556', fontSize: 11, margin: '8px 0 0' }}>Use for KVKK Art. 11 access / erasure requests. Both actions are audit-logged.</p>
            </div>
          )}

          {/* ── App login setup link (Phase 3.2 — replaces name+passport) ── */}
          {student && student.id && (isAdmin() || isAgentOrEmployee()) && (
            <div style={{ margin: '16px 0 0', padding: '14px 16px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, background: 'rgba(15,52,96,0.25)' }}>
              <div style={{ color: '#aaa', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 }}>STUDENT APP LOGIN</div>
              <button type="button" disabled={issuingLoginSetup} onClick={async () => {
                setIssuingLoginSetup(true);
                try {
                  const res = await studentsAPI.issueLoginSetup(student.id);
                  setLoginSetup(res.data);
                } catch (err) {
                  alert(err?.response?.data?.error || 'Could not generate a login link.');
                } finally {
                  setIssuingLoginSetup(false);
                }
              }} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#333', cursor: issuingLoginSetup ? 'wait' : 'pointer', fontSize: 13 }}>
                {issuingLoginSetup ? 'Generating…' : 'Generate login link'}
              </button>
              {loginSetup && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                    Send this to the student over WhatsApp. Expires in {loginSetup.expires_hours}h. One-time use.
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input readOnly value={loginSetup.link || loginSetup.token}
                      onFocus={(e) => e.target.select()}
                      style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#f8f9fa', color: '#333', fontSize: 12 }} />
                    <button type="button" onClick={() => {
                      navigator.clipboard?.writeText(loginSetup.link || loginSetup.token);
                    }} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#333', cursor: 'pointer', fontSize: 13 }}>
                      Copy
                    </button>
                  </div>
                  {!loginSetup.link && (
                    <p style={{ color: '#f5a623', fontSize: 11, margin: '6px 0 0' }}>
                      No setup-page URL configured (System Settings → student_password_setup_url). The student pastes this raw token in the app's "Set / reset password" screen.
                    </p>
                  )}
                </div>
              )}
              <p style={{ color: '#999', fontSize: 11, margin: '8px 0 0' }}>The student sets their own password from this link. Audit-logged.</p>
            </div>
          )}

          {/* ── Transfer to Alumni ─────────────────────────────────────── */}
          {student && (isAdmin() || isSalesLeadership()) && !formData.quest_became_alumni && !transferSuccess && (
            <div style={{
              margin: '16px 0 0',
              padding: '16px',
              background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecff 100%)',
              borderRadius: 10,
              border: '1px solid #c7d2fe',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#011FF3', fontSize: 14 }}>🎓 Transfer to Alumni</div>
                  <div style={{ color: '#555', fontSize: 12, marginTop: 3 }}>
                    Student has completed their journey — move them to the Alumni registry.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTransferDialog(true)}
                  disabled={transferring}
                  style={{
                    background: '#011FF3',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Transfer →
                </button>
              </div>
            </div>
          )}

          {(formData.quest_became_alumni || transferSuccess) && (
            <div style={{
              margin: '16px 0 0',
              padding: '14px 16px',
              background: 'linear-gradient(135deg, #B8860B22, #FFD70033)',
              borderRadius: 10,
              border: '1px solid #FFD700',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
              <span style={{ fontSize: 20 }}>⭐</span>
              <span style={{ fontWeight: 600, color: '#7a5a00' }}>
                This student has been transferred to Alumni.
              </span>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : student ? 'Update' : 'Create'}
            </button>
          </div>
        </form>

        {showTransferDialog && (
          <TransferAlumniDialog
            studentName={`${formData.first_name} ${formData.last_name}`.trim()}
            onConfirm={handleTransferToAlumni}
            onCancel={() => setShowTransferDialog(false)}
            loading={transferring}
          />
        )}
      </div>
    </div>
  );
};

export default StudentModal;

